import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useNotificationActions } from "~/shared/hooks/use-notifications.ts";
import type { GlobalCarrier } from "~/shared/schemas/auto-dispatch-schema.ts";
import {
	globalCarrierSchema,
	patchGlobalCarrierSchema,
} from "~/shared/schemas/auto-dispatch-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { patchAndValidate } from "~/shared/services/api/fetch-client.ts";

const updateGlobalCarriers = async (
	updatedCarrier: GlobalCarrier,
	signal?: AbortSignal,
) => {
	const signalOptions = signal ? { signal } : undefined;
	const body = {
		carrierId: updatedCarrier.carrierId,
		canAutoDispatch: updatedCarrier.canAutoDispatch,
		dispatchTime: updatedCarrier.dispatchTime,
		dispatchDaysInAdvance: updatedCarrier.dispatchDaysInAdvance,
		accessorialIds: updatedCarrier.accessorials?.map((a) => a.id) ?? [],
	};

	const parsedJson = patchGlobalCarrierSchema.safeParse(body);
	if (!parsedJson.success) {
		throw new Error(`Invalid carrier data: ${parsedJson.error.message}`);
	}

	const resp = await patchAndValidate(
		apiRoutes.PATCHGLOBALAUTODISPATCHABLECARRIERS,
		parsedJson.data,
		globalCarrierSchema,
		signalOptions,
	);
	if (resp.success) {
		return resp.data;
	} else {
		throw new Error(
			`Failed to update global carrier ${updatedCarrier.carrierName}: ${resp.error}`,
		);
	}
};

export const useUpdateGlobalCarriers = () => {
	const queryClient = useQueryClient();
	const { showError, showSuccess } = useNotificationActions();

	const { mutate: patchCarrier } = useMutation({
		mutationFn: (updatedCarrier: GlobalCarrier) =>
			updateGlobalCarriers(updatedCarrier),

		// Optimistically update the cache immediately
		onMutate: async (updatedCarrier) => {
			await queryClient.cancelQueries({
				queryKey: ["globalAutoDispatchableCarriers"],
			});
			const previous = queryClient.getQueryData([
				"globalAutoDispatchableCarriers",
			]);

			queryClient.setQueryData(
				["globalAutoDispatchableCarriers"],
				(old: GlobalCarrier[]) =>
					old.map((c) =>
						c.carrierId === updatedCarrier.carrierId ? updatedCarrier : c,
					),
			);

			return { previous }; // rollback context - for onError to restore previous state if the mutation fails
		},

		onSuccess: () => {
			showSuccess("Carrier Selection Updated");
		},

		// Roll back on error
		onError: (_err, _update, context) => {
			showError(`${_err}`);
			queryClient.setQueryData(
				["globalAutoDispatchableCarriers"],
				context?.previous,
			);
		},
	});

	return { patchCarrier };
};

export const useUpdateCarrier = () => {
	const { patchCarrier } = useUpdateGlobalCarriers();
	return patchCarrier;
};
